/**
 * The canonical vocabulary. Every enum here is the single source of truth shared by
 * the React app, the API, the ingestion pipeline, and the SQL schema.
 *
 * The prototype (docs/reference/lending-hound-complete-3.jsx) carried TWO conflicting
 * program lists — a broker-facing `PROGRAMS` ("Full Doc (NonQM)", "1099 Only",
 * "No Doc / Stated") and an admin-facing `PROGRAM_OPTIONS` ("Full Doc", "1099",
 * "No Doc"). They are reconciled here into `DOC_TYPES`.
 *
 * The reconciliation is not a rename. In every lender matrix we inspected, the grid's
 * column dimension is *how income is documented* — Full Doc / Bank Statement / 1099 /
 * P&L Only / WVOE / Asset Depletion / DSCR. That is the axis the eligibility grid is
 * actually keyed on, so it is the axis we model. Borrower-status facts that the
 * prototype mixed into the same list (Foreign National, ITIN) genuinely do behave as
 * their own matrix pages at several lenders, so they stay in the enum — but they are
 * orthogonal to `citizenship`, which selects the grid rather than the column; see
 * `needsDedicatedGrid` below and `program.borrower_statuses`.
 */

/**
 * Documentation type - the column dimension of every lender LTV grid.
 *
 * Two codes are kept out of the intake form, and the markers that hide them are two
 * separate flags rather than one `hidden: true`. Both mean "extraction still needs this
 * code, a broker should not be offered it", but they mean it for unrelated reasons and
 * only one of them is reversible: `borrowerStatus` is an argument about what the axis
 * IS and will not come back, while `coveredBy` is a decision about a word on a dropdown,
 * which is exactly the kind of thing that gets asked for again the other way. Folded
 * into one flag, the next reader cannot tell which argument they would be undoing.
 *
 * `borrowerStatus: true` marks the two codes that are NOT really documentation types.
 * A handful of lenders do label a grid column "Foreign National" or "ITIN", so the
 * codes have to exist for extraction to map those headers - but borrower status is a
 * property of the borrower and belongs on the citizenship field, which selects the
 * grid. Offering them as documentation choices lets a broker ask a question that
 * matches zero cells and reads back as "no lender can do this", so `INTAKE_DOC_TYPES`
 * is what the form renders.
 *
 * `coveredBy` names another code that reaches the same cells, which is what makes hiding
 * one free. Henry, 2026-09-01: DSCR already covers the low-ratio file and nobody knows
 * what "no ratio" means. That is safe because of the shape of the corpus rather than the
 * shape of the words - every one of the 210 stored cells carrying `no_ratio` carries
 * `dscr` too, because the grids print the column as "DSCR / No Ratio" and expandDocTypes
 * emits both codes (see normalize.ts). A broker who picks DSCR sweeps every cell a
 * no_ratio choice could have reached, so nothing became unreachable when the option
 * left the form. Deleting the code rather than hiding it would strip half the labelling
 * off those 210 cells and buy nothing: the label a lender printed is still the label,
 * and extraction has to be able to say so.
 */
export const DOC_TYPES = [
  { code: 'full_doc',         label: 'Full Doc (NonQM)',      sort: 10 },
  { code: 'bank_stmt_12',     label: 'Bank Statement (12mo)', sort: 20 },
  { code: 'bank_stmt_24',     label: 'Bank Statement (24mo)', sort: 21 },
  { code: 'income_1099',      label: '1099 Only',             sort: 30 },
  { code: 'pnl_only',         label: 'P&L Only',              sort: 40 },
  { code: 'wvoe',             label: 'WVOE',                  sort: 50 },
  { code: 'w2_only',          label: 'W2 Only (NonQM)',       sort: 60 },
  { code: 'asset_depletion',  label: 'Asset Depletion',       sort: 70 },
  { code: 'dscr',             label: 'DSCR',                  sort: 80 },
  { code: 'no_ratio',         label: 'No Ratio / No Income',  sort: 90, coveredBy: 'dscr' },
  { code: 'foreign_national', label: 'Foreign National',      sort: 100, borrowerStatus: true },
  { code: 'itin',             label: 'ITIN',                  sort: 110, borrowerStatus: true },
] as const satisfies readonly {
  code: string; label: string; sort: number;
  borrowerStatus?: true; coveredBy?: string;
}[];

export type DocTypeCode = (typeof DOC_TYPES)[number]['code'];
export const DOC_TYPE_CODES = DOC_TYPES.map((d) => d.code) as readonly DocTypeCode[];

/**
 * The documentation types a broker may actually choose on the intake form.
 *
 * Both hiding flags filter here, and neither one narrows a search: a `borrowerStatus`
 * code matches no cells on the grid the broker would be searching, and a `coveredBy`
 * code is reachable in full through the code it names.
 */
export const INTAKE_DOC_TYPES = DOC_TYPES.filter(
  (d) => !('borrowerStatus' in d) && !('coveredBy' in d),
) as readonly { code: DocTypeCode; label: string; sort: number }[];

/** Occupancy — a grid dimension at every lender. */
export const OCCUPANCIES = [
  { code: 'primary',     label: 'Primary Residence' },
  { code: 'second_home', label: 'Second Home' },
  { code: 'investment',  label: 'Investment Property' },
] as const;
export type Occupancy = (typeof OCCUPANCIES)[number]['code'];
export const OCCUPANCY_CODES = OCCUPANCIES.map((o) => o.code) as readonly Occupancy[];

/**
 * Loan purpose. Matrices commonly collapse purchase and rate/term into one column
 * ("PUR/R&T", "Purch & R/T") and break out cash-out; extraction expands such a column
 * into two rows, one per purpose, rather than inventing a fourth enum member.
 */
export const LOAN_PURPOSES = [
  { code: 'purchase',  label: 'Purchase' },
  { code: 'rate_term', label: 'Rate/Term Refi' },
  { code: 'cash_out',  label: 'Cash-Out Refi' },
] as const;
export type LoanPurpose = (typeof LOAN_PURPOSES)[number]['code'];
export const LOAN_PURPOSE_CODES = LOAN_PURPOSES.map((p) => p.code) as readonly LoanPurpose[];

export const CITIZENSHIPS = [
  { code: 'us_citizen',                label: 'US Citizen' },
  { code: 'permanent_resident',        label: 'Permanent Resident' },
  { code: 'non_permanent_resident',    label: 'Non-Perm Resident Visa' },
  { code: 'foreign_national_credit',   label: 'Foreign National (with US Credit)' },
  { code: 'foreign_national_no_credit',label: 'Foreign National (No US Credit)' },
  { code: 'itin',                      label: 'ITIN Borrower' },
] as const;
export type Citizenship = (typeof CITIZENSHIPS)[number]['code'];
export const CITIZENSHIP_CODES = CITIZENSHIPS.map((c) => c.code) as readonly Citizenship[];

export const PROPERTY_TYPES = [
  { code: 'sfr',              label: 'Single Family' },
  { code: 'condo_warr',       label: 'Condo (Warrantable)' },
  { code: 'condo_non_warr',   label: 'Condo (Non-Warrantable)' },
  { code: 'condotel',         label: 'Condotel' },
  { code: 'units_2',          label: '2-Unit' },
  { code: 'units_3',          label: '3-Unit' },
  { code: 'units_4',          label: '4-Unit' },
  /*
   * The code says 5-8 and the label says 5+, and the split is deliberate rather than
   * drift.
   *
   * The old label was simply wrong. Six programs in the corpus reach past four units and
   * they stop in three different places - JLD at 10, Deephaven's "DSCR 5-9 Unit" at 9,
   * Logan, NQM and Verus at 8 - so no range printed on this option can be true of all of
   * them, and "5-10" would have been wrong in the other direction: a broker with a
   * nine-unit building would read "5-8" and never ask. "5+" is the honest claim, because
   * the ceiling belongs to the lender rather than to the form. What makes it safe to
   * stop naming a ceiling is that the intake now asks for the real count the moment this
   * option is chosen - see `unitsForPropertyType`.
   *
   * The CODE stays `units_5_8` because 92 stored matrix cells reference it and the
   * extraction patterns that produce it are keyed on the "5-8 Units" header lenders
   * actually print. Renaming it is a migration plus a re-ingest of every matrix, and it
   * buys nothing a broker can see.
   */
  { code: 'units_5_8',        label: '5+ Units' },
  { code: 'mixed_use',        label: 'Mixed Use' },
  { code: 'commercial',       label: 'Commercial' },
  { code: 'manufactured',     label: 'Manufactured' },
  { code: 'rural',            label: 'Rural/Acreage' },
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number]['code'];

/**
 * How many residential units the borrower's property type implies.
 *
 * Programs are scoped by unit count and the form asks for a property type, from which
 * the count is implied - everywhere except the open-ended 5+ bucket, which is the one
 * option whose label cannot imply a number and so asks for it. Returning null means "the
 * question does not apply", which is a different answer from 1 and must stay different: a mixed
 * use or commercial property should not be filtered against a residential unit range at
 * all, while a single family emphatically should.
 *
 * `units_5_8` returns 5, the fewest it can be. A program is excluded only when the
 * borrower CANNOT fit it, so the low end of the borrower's range is the safe end to
 * test - it never excludes a program the borrower might have qualified for.
 *
 * `statedUnits` is what replaces that guess once somebody has actually counted. The form
 * asks for the number as soon as 5+ is chosen, and where an answer exists the guess must
 * defer to it, because understating is only safe while the true figure is unknown: the
 * filter passes a program when the count sits inside its unit range, so a nine-unit
 * building tested as a 5 fits Logan's eight-unit ceiling and gets quoted off a grid that
 * does not cover the building. Guessing low avoids excluding a lender wrongly; it also
 * INCLUDES one wrongly, and only the second of those reaches a broker as a number.
 *
 * A stated count overrides the open-ended bucket and nothing else. For every other
 * property type the code IS the count, so a number disagreeing with it is a
 * contradiction to kick back rather than a fact to prefer - and a count below this
 * bucket's own floor is that same contradiction, ignored here and refused by the intake
 * schema at the boundary, where it can be said to the person who typed it.
 */
export function unitsForPropertyType(
  t: PropertyType,
  statedUnits?: number | null,
): number | null {
  switch (t) {
    case 'sfr':
    case 'condo_warr':
    case 'condo_non_warr':
    case 'condotel':
    case 'manufactured':
    case 'rural':
      return 1;
    case 'units_2': return 2;
    case 'units_3': return 3;
    case 'units_4': return 4;
    case 'units_5_8': return statedUnits != null && statedUnits >= 5 ? statedUnits : 5;
    case 'mixed_use':
    case 'commercial':
      return null;
  }
}
export const PROPERTY_TYPE_CODES = PROPERTY_TYPES.map((p) => p.code) as readonly PropertyType[];

export const CREDIT_EVENTS = [
  { code: 'none',            label: 'None' },
  { code: 'bk7',             label: 'Bankruptcy (Ch. 7)' },
  { code: 'bk13',            label: 'Bankruptcy (Ch. 13)' },
  { code: 'foreclosure',     label: 'Foreclosure' },
  { code: 'short_sale',      label: 'Short Sale' },
  { code: 'deed_in_lieu',    label: 'Deed-in-Lieu' },
  { code: 'mortgage_late_30',label: 'Mortgage Lates 30' },
  { code: 'mortgage_late_60',label: 'Mortgage Lates 60' },
  { code: 'mortgage_late_90',label: 'Mortgage Lates 90+' },
  { code: 'multiple_lates',  label: 'Multiple Late Pays' },
] as const;
export type CreditEvent = (typeof CREDIT_EVENTS)[number]['code'];
export const CREDIT_EVENT_CODES = CREDIT_EVENTS.map((c) => c.code) as readonly CreditEvent[];

/** 50 states + DC. Lender matrices restrict by state constantly. */
export const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA',
  'WV','WI','WY',
] as const;
export type UsState = (typeof US_STATES)[number];

/** Territories appear in lender ineligible-state lists (JLD excludes PR, GU, VI). */
export const US_TERRITORIES = ['PR', 'GU', 'VI'] as const;
export type UsTerritory = (typeof US_TERRITORIES)[number];

/**
 * How an investor overlay relates to the base guideline it modifies.
 * `restricts` merges via attribute direction; `replaces` and `adds` are NEVER
 * auto-merged and must be surfaced verbatim in the answer.
 */
export const OVERLAY_RELATIONS = ['restricts', 'replaces', 'adds'] as const;
export type OverlayRelation = (typeof OVERLAY_RELATIONS)[number];

export const VERDICTS = ['eligible', 'eligible_with_conditions', 'ineligible', 'no_data'] as const;
export type Verdict = (typeof VERDICTS)[number];

// ── Lookup helpers ───────────────────────────────────────────────────────────

const byCode = <T extends { code: string; label: string }>(rows: readonly T[]) =>
  new Map(rows.map((r) => [r.code, r.label]));

const DOC_TYPE_LABELS   = byCode(DOC_TYPES);
const OCCUPANCY_LABELS  = byCode(OCCUPANCIES);
const PURPOSE_LABELS    = byCode(LOAN_PURPOSES);
const CITIZENSHIP_LABELS= byCode(CITIZENSHIPS);
const PROPERTY_LABELS   = byCode(PROPERTY_TYPES);
const CREDIT_LABELS     = byCode(CREDIT_EVENTS);

export const docTypeLabel     = (c: DocTypeCode)   => DOC_TYPE_LABELS.get(c) ?? c;
export const occupancyLabel   = (c: Occupancy)     => OCCUPANCY_LABELS.get(c) ?? c;
export const purposeLabel     = (c: LoanPurpose)   => PURPOSE_LABELS.get(c) ?? c;
export const citizenshipLabel = (c: Citizenship)   => CITIZENSHIP_LABELS.get(c) ?? c;
export const propertyLabel    = (c: PropertyType)  => PROPERTY_LABELS.get(c) ?? c;
export const creditEventLabel = (c: CreditEvent)   => CREDIT_LABELS.get(c) ?? c;

/**
 * True when a citizenship routes the file to a dedicated grid rather than a column on
 * the main one.
 *
 * This replaces an earlier `deriveDocTypeFromCitizenship`, which rewrote docType to
 * 'foreign_national'. The comment above it had the insight right - borrower status
 * selects the grid - and the code then applied it to the grid's *column*. Those axes
 * are independent: JLD's Foreign National page has DSCR columns, because the borrower
 * is a foreign national AND qualifies on property cash flow. The rewrite made every
 * dedicated FN and ITIN grid match zero cells, so lenders whose FN matrix had been
 * ingested and verified cell by cell were reported as a document gap.
 *
 * The restriction now lives on `program.borrower_statuses`, and the evaluator filters
 * programs by it while docType keeps meaning only how income is documented.
 */
export function needsDedicatedGrid(c: Citizenship): boolean {
  return c === 'foreign_national_credit'
      || c === 'foreign_national_no_credit'
      || c === 'itin';
}

/** True when the doc type qualifies on property cash flow rather than borrower income. */
export const isCashFlowQualified = (c: DocTypeCode): boolean =>
  c === 'dscr' || c === 'no_ratio';

/**
 * True when the transaction hands the borrower money at closing.
 *
 * One line, and it exists because three layers now ask the same question: the intake
 * form decides whether to render the cash-in-hand box, the schema decides whether to
 * carry the figure, and the cash-in-hand cap decides whether it can bind at all. Spelled
 * as `=== 'cash_out'` in three files it is three places to miss if the enum ever splits
 * cash-out into, say, delayed financing - which several matrices already treat as its
 * own purpose with rate/term parameters (see `refi.value_basis_after_recent_listing`).
 *
 * Null is deliberately NOT this function's business: an unanswered purpose is a widened
 * axis, and every caller has to decide what a widened axis means for it. Making the
 * signature take a null and answer false would make "the broker did not say" and "the
 * broker said purchase" the same input, which for a cash cap is the difference between a
 * question and a pass.
 */
export const isCashOut = (p: LoanPurpose): boolean => p === 'cash_out';
